export type MessageArchivePeriodUnit = 'day' | 'week' | 'month';

export type MessageArchivePeriodRange = {
  periodKey: string;
  periodStartedAt: string;
  periodEndedAt: string;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function toUtcDateOnly(timestamp: number): Date {
  const date = new Date(timestamp);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function isoWeekStart(date: Date): Date {
  const day = date.getUTCDay() || 7;
  return new Date(date.getTime() - (day - 1) * MS_PER_DAY);
}

function isoWeekYear(startOfWeek: Date): number {
  const thursday = new Date(startOfWeek.getTime() + 3 * MS_PER_DAY);
  return thursday.getUTCFullYear();
}

function isoWeekNumber(startOfWeek: Date): number {
  const year = isoWeekYear(startOfWeek);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const firstWeekStart = isoWeekStart(jan4);
  return Math.floor((startOfWeek.getTime() - firstWeekStart.getTime()) / (7 * MS_PER_DAY)) + 1;
}

export function getUtcArchivePeriodRange(timestamp: number, unit: MessageArchivePeriodUnit): MessageArchivePeriodRange {
  const date = toUtcDateOnly(timestamp);

  if (unit === 'day') {
    const start = date;
    const end = new Date(start.getTime() + MS_PER_DAY);
    const periodKey = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}-${pad2(start.getUTCDate())}`;
    return {
      periodKey,
      periodStartedAt: start.toISOString(),
      periodEndedAt: end.toISOString()
    };
  }

  if (unit === 'month') {
    const start = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
    const end = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
    const periodKey = `${start.getUTCFullYear()}-${pad2(start.getUTCMonth() + 1)}`;
    return {
      periodKey,
      periodStartedAt: start.toISOString(),
      periodEndedAt: end.toISOString()
    };
  }

  const start = isoWeekStart(date);
  const end = new Date(start.getTime() + 7 * MS_PER_DAY);
  const periodKey = `${isoWeekYear(start)}-W${pad2(isoWeekNumber(start))}`;
  return {
    periodKey,
    periodStartedAt: start.toISOString(),
    periodEndedAt: end.toISOString()
  };
}

export function getPreviousUtcArchivePeriodRange(
  period: MessageArchivePeriodRange,
  unit: MessageArchivePeriodUnit
): MessageArchivePeriodRange {
  const startedAt = Date.parse(period.periodStartedAt);
  if (!Number.isFinite(startedAt)) throw new Error('Invalid archive period start');

  if (unit === 'day') return getUtcArchivePeriodRange(startedAt - MS_PER_DAY, unit);
  if (unit === 'week') return getUtcArchivePeriodRange(startedAt - 7 * MS_PER_DAY, unit);

  const start = new Date(startedAt);
  return getUtcArchivePeriodRange(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1), unit);
}

export function getNextUtcArchivePeriodRange(
  period: MessageArchivePeriodRange,
  unit: MessageArchivePeriodUnit
): MessageArchivePeriodRange {
  const endedAt = Date.parse(period.periodEndedAt);
  if (!Number.isFinite(endedAt)) throw new Error('Invalid archive period end');
  return getUtcArchivePeriodRange(endedAt, unit);
}

export function getMutableArchiveWindowStartPeriod(params: {
  now: number;
  unit: MessageArchivePeriodUnit;
  finalizeGraceDays: number;
}): MessageArchivePeriodRange {
  let period = getUtcArchivePeriodRange(params.now, params.unit);
  const graceMs = Math.max(0, params.finalizeGraceDays) * MS_PER_DAY;

  for (let i = 0; i < 370; i += 1) {
    const previous = getPreviousUtcArchivePeriodRange(period, params.unit);
    const previousEndedAt = Date.parse(previous.periodEndedAt);
    if (!Number.isFinite(previousEndedAt) || params.now >= previousEndedAt + graceMs) {
      return period;
    }
    period = previous;
  }

  return period;
}

export function listArchivePeriodsBefore(params: {
  from: MessageArchivePeriodRange;
  beforePeriodKey: string;
  unit: MessageArchivePeriodUnit;
}): MessageArchivePeriodRange[] {
  const periods: MessageArchivePeriodRange[] = [];
  let cursor = params.from;

  for (let i = 0; i < 370; i += 1) {
    if (cursor.periodKey === params.beforePeriodKey) break;
    periods.push(cursor);
    cursor = getNextUtcArchivePeriodRange(cursor, params.unit);
  }

  return periods;
}

export function isArchivePeriodFinalizable(params: {
  periodEndedAt: string;
  finalizeGraceDays: number;
  now?: number;
}): boolean {
  const endedAt = Date.parse(params.periodEndedAt);
  if (!Number.isFinite(endedAt)) return false;
  const graceMs = Math.max(0, params.finalizeGraceDays) * MS_PER_DAY;
  return (params.now ?? Date.now()) >= endedAt + graceMs;
}
